import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isCurrentUserAdmin, signOut } from "../supabase/authService";
import {
  AdminMenuItem,
  ImageCropFocus,
  createMenuItem,
  deleteMenuItem,
  getAdminMenuItems,
  getCategories,
  removeMenuImage,
  uploadMenuImage,
  updateMenuItem,
} from "../supabase/menuService";
import "./Admin.css";

type Category = { id: number; name: string };

type MenuItem = AdminMenuItem;

const fmtTRY = (n: number) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(
    n ?? 0
  );

// TR ondalık/parasal girişleri güvenli parse et
const parseNum = (value: string | number | undefined) => {
  if (typeof value === "number") return value;
  if (!value) return 0;
  const v = String(value).replace(/\./g, "").replace(",", ".");
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const parseExtras = (value: string) =>
  value
    .split(",")
    .map((extra) => extra.trim())
    .filter(Boolean);

export default function Admin() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  // Kategoriler
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCatId, setSelectedCatId] = useState<number | null>(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);

  // Ürünler
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Form: Yeni ürün
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState<{ name: string; price: string; description: string }>({
    name: "",
    price: "",
    description: "",
  });
  const [newExtras, setNewExtras] = useState<string>("");
  const [newImage, setNewImage] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [newImageFocus, setNewImageFocus] = useState<ImageCropFocus>({ x: 0.5, y: 0.5 });

  // Satır içi düzenleme durumları
  const [editId, setEditId] = useState<number | null>(null);
  const [editPrice, setEditPrice] = useState<string>("");
  const [editDesc, setEditDesc] = useState<string>("");
  const [editExtras, setEditExtras] = useState<string>("");
  const [editImage, setEditImage] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editImageFocus, setEditImageFocus] = useState<ImageCropFocus>({ x: 0.5, y: 0.5 });
  const [draggingCrop, setDraggingCrop] = useState<"new" | "edit" | null>(null);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCatId) || null,
    [categories, selectedCatId]
  );

  const logout = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    if (!newImage) {
      setNewImagePreview(null);
      setNewImageFocus({ x: 0.5, y: 0.5 });
      return;
    }

    const url = URL.createObjectURL(newImage);
    setNewImagePreview(url);
    setNewImageFocus({ x: 0.5, y: 0.5 });

    return () => URL.revokeObjectURL(url);
  }, [newImage]);

  useEffect(() => {
    if (!editImage) {
      setEditImagePreview(null);
      setEditImageFocus({ x: 0.5, y: 0.5 });
      return;
    }

    const url = URL.createObjectURL(editImage);
    setEditImagePreview(url);
    setEditImageFocus({ x: 0.5, y: 0.5 });

    return () => URL.revokeObjectURL(url);
  }, [editImage]);

  // Admin kontrolü
  useEffect(() => {
    let active = true;
    isCurrentUserAdmin().then((admin) => {
      if (active) setIsAdmin(admin);
    });
    return () => {
      active = false;
    };
  }, []);

  // Kategorileri çek
  useEffect(() => {
    (async () => {
      const cats: Category[] = await getCategories();
      setCategories(cats);
      if (cats.length && selectedCatId === null) setSelectedCatId(cats[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ilk yüklemede

  // Ürünleri çek
  useEffect(() => {
    if (selectedCatId === null) return;
    (async () => {
      setLoadingItems(true);
      try {
        const rows: MenuItem[] = await getAdminMenuItems(selectedCatId);
        setItems(rows);
      } finally {
        setLoadingItems(false);
      }
    })();
  }, [selectedCatId]);

  // Yeni ürün ekle
  const addItem = async () => {
    if (!isAdmin) return alert("Yetkiniz yok.");
    if (selectedCatId === null) return alert("Kategori seçin.");
    const name = newItem.name.trim();
    const price = parseNum(newItem.price);
    if (!name) return alert("Ürün adı boş olamaz.");
    if (price <= 0) return alert("Geçerli bir fiyat girin.");

    setBusy(true);
    try {
      const image = newImage ? await uploadMenuImage(newImage, newImageFocus) : null;
      const extras = parseExtras(newExtras);
      const created = await createMenuItem({
        categoryId: selectedCatId,
        name,
        price,
        description: newItem.description.trim() || undefined,
        extras,
        imageUrl: image?.imageUrl,
        imagePath: image?.imagePath,
      });

      setItems((prev) => [
        ...prev,
        created,
      ]);
      setNewItem({ name: "", price: "", description: "" });
      setNewExtras("");
      setNewImage(null);
      setNewImagePreview(null);
      setNewImageFocus({ x: 0.5, y: 0.5 });
      alert("✔ Ürün eklendi.");
    } catch (e) {
      console.error(e);
      alert("Ürün eklenirken hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  // Ürün sil
  const deleteItem = async (id: number) => {
    if (!isAdmin) return alert("Yetkiniz yok.");
    const target = items.find((x) => x.id === id);
    if (!target) return;

    const ok = window.confirm(
      `"${target.name}" ürününü silmek istediğinize emin misiniz?`
    );
    if (!ok) return;

    setBusy(true);
    try {
      await removeMenuImage(target.image_path);
      await deleteMenuItem(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      alert("✔ Ürün silindi.");
    } catch (e) {
      console.error(e);
      alert("Silinirken hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  // Düzenlemeyi başlat
  const startEdit = (it: MenuItem) => {
    setEditId(it.id);
    // fiyatı kullanıcıya TR formatıyla gösterelim
    setEditPrice(String(it.price).replace(".", ",")); // basit gösterim
    setEditDesc(it.description ?? "");
    setEditExtras((it.extras ?? []).join(", "));
    setEditImage(null);
    setEditImagePreview(null);
    setEditImageFocus({ x: 0.5, y: 0.5 });
  };

  const getCropFocus = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  const clampFocus = (focus: ImageCropFocus): ImageCropFocus => ({
    x: Math.min(1, Math.max(0, focus.x)),
    y: Math.min(1, Math.max(0, focus.y)),
  });

  const startCropDrag = (
    event: React.PointerEvent<HTMLDivElement>,
    type: "new" | "edit"
  ) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setDraggingCrop(type);
    const focus = clampFocus(getCropFocus(event));
    if (type === "new") setNewImageFocus(focus);
    else setEditImageFocus(focus);
  };

  const moveCropDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingCrop) return;
    const focus = clampFocus(getCropFocus(event));
    if (draggingCrop === "new") setNewImageFocus(focus);
    else setEditImageFocus(focus);
  };

  const stopCropDrag = () => {
    setDraggingCrop(null);
  };

  // Düzenlemeyi iptal
  const cancelEdit = () => {
    setEditId(null);
    setEditPrice("");
    setEditDesc("");
    setEditExtras("");
    setEditImage(null);
    setEditImagePreview(null);
    setEditImageFocus({ x: 0.5, y: 0.5 });
  };

  // Düzenlemeyi kaydet (fiyat + description)
  const saveEdit = async (id: number) => {
    if (!isAdmin) return alert("Yetkiniz yok.");
    const price = parseNum(editPrice);
    if (price <= 0) return alert("Geçerli bir fiyat girin.");

    setBusy(true);
    try {
      const payload: any = { price };
      const desc = editDesc.trim();
      const extras = parseExtras(editExtras);
      const target = items.find((x) => x.id === id);
      const image = editImage ? await uploadMenuImage(editImage, editImageFocus) : null;
      // boş ise alanı tamamen kaldırmak yerine "" yazmak isterseniz: payload.description = desc;
      if (desc) payload.description = desc;
      else payload.description = ""; // boşaltma
      payload.extras = extras;
      if (image) {
        payload.imageUrl = image.imageUrl;
        payload.imagePath = image.imagePath;
      }

      await updateMenuItem(id, payload);
      if (image && target?.image_path) await removeMenuImage(target.image_path);

      // local state’i güncelle
      setItems((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                price,
                description: desc,
                extras,
                image_url: image?.imageUrl ?? x.image_url,
                image_path: image?.imagePath ?? x.image_path,
              }
            : x
        )
      );

      cancelEdit();
      alert("✔ Ürün güncellendi.");
    } catch (e) {
      console.error(e);
      alert("Güncellenirken hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  if (isAdmin === null) return <div className="page">Yükleniyor…</div>;
  if (isAdmin === false)
    return (
      <div className="page">
        <h2 className="title">Erişim yok</h2>
      </div>
    );

  return (
    <div className="page">
      {/* Üst araç çubuğu: sadece kategori seçimi */}
      <div className="toolbar">
        <div className="field category-select-field">
          <label>Kategori</label>
          <button
            type="button"
            className="category-select-trigger"
            onClick={() => setCategoryMenuOpen((open) => !open)}
            aria-haspopup="listbox"
            aria-expanded={categoryMenuOpen}
          >
            <span>{selectedCategory ? selectedCategory.name : "Kategori seç"}</span>
            <span className="category-select-chevron" aria-hidden>
              ▾
            </span>
          </button>
          {categoryMenuOpen && (
            <div className="category-select-menu" role="listbox">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`category-select-option ${
                    selectedCatId === c.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedCatId(c.id);
                    setCategoryMenuOpen(false);
                  }}
                  role="option"
                  aria-selected={selectedCatId === c.id}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="spacer" />
        <button className="btn" type="button" onClick={logout}>
          Çıkış Yap
        </button>
      </div>

      {/* Yeni ürün formu */}
      <div className="group">
        <div className="group-title">Yeni Ürün</div>
        <div
          className="grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 150px 1fr 1fr 210px auto",
            gap: 12,
            padding: 12,
          }}
        >
          <input
            className="input"
            placeholder="Ad"
            value={newItem.name}
            onChange={(e) => setNewItem((s) => ({ ...s, name: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Fiyat (ör: 95,50)"
            value={newItem.price}
            onChange={(e) => setNewItem((s) => ({ ...s, price: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Açıklama (opsiyonel)"
            value={newItem.description}
            onChange={(e) => setNewItem((s) => ({ ...s, description: e.target.value }))}
          />
          <input
            className="input"
            placeholder="Ekstralar (virgülle ayır)"
            value={newExtras}
            onChange={(e) => setNewExtras(e.target.value)}
          />
          <label className="file-picker">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNewImage(e.target.files?.[0] ?? null)}
            />
            <span>Görsel Seç</span>
            <small>{newImage ? newImage.name : "Seçilmedi"}</small>
          </label>
          <button className="btn-primary" disabled={busy || selectedCatId === null} onClick={addItem}>
            {busy ? "Ekleniyor..." : "Ürün Ekle"}
          </button>
        </div>
        {newImagePreview && (
          <div className="image-crop-panel">
            <div
              className="image-crop-preview"
              onPointerDown={(e) => startCropDrag(e, "new")}
              onPointerMove={moveCropDrag}
              onPointerUp={stopCropDrag}
              onPointerCancel={stopCropDrag}
              role="button"
              tabIndex={0}
              aria-label="Görsel odak noktası seç"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setNewImageFocus({ x: 0.5, y: 0.5 });
                }
              }}
            >
              <img
                src={newImagePreview}
                alt="Seçilen ürün"
                style={{
                  objectPosition: `${newImageFocus.x * 100}% ${newImageFocus.y * 100}%`,
                }}
              />
              <span
                className="image-crop-focus"
                style={{
                  left: `${newImageFocus.x * 100}%`,
                  top: `${newImageFocus.y * 100}%`,
                }}
              />
            </div>
            <div className="image-crop-help">
              Görsel 16:9 kırpılır. Çerçevede görünmesini istediğiniz bölgeye sürükleyin.
            </div>
          </div>
        )}
      </div>

      {/* Ürün listesi (düzenle + sil) */}
      <div className="group">
        <div className="group-title">
          {selectedCategory ? selectedCategory.name : "Kategori"}
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Görsel</th>
              <th>Ad</th>
              <th>Fiyat</th>
              <th>Açıklama</th>
              <th>Ekstralar</th>
              <th style={{ width: 220 }}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loadingItems && (
              <tr>
                <td colSpan={6} className="empty">
                  Yükleniyor…
                </td>
              </tr>
            )}
            {!loadingItems && items.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  Bu kategoride ürün yok
                </td>
              </tr>
            )}
            {!loadingItems &&
              items.map((it) => {
                const isEditing = editId === it.id;
                return (
                  <tr key={it.id}>
                    <td>
                      <div className="admin-image-cell">
                        {it.image_url ? (
                          <img src={it.image_url} alt={it.name} />
                        ) : (
                          <span>Yok</span>
                        )}
                      </div>
                    </td>
                    <td className="cell-name">{it.name}</td>

                    {/* Fiyat hücresi */}
                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          placeholder="Örn: 120,00"
                        />
                      ) : (
                        <div>{fmtTRY(it.price)}</div>
                      )}
                    </td>

                    {/* Açıklama hücresi */}
                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          placeholder="Açıklama"
                        />
                      ) : (
                        it.description ?? "-"
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          value={editExtras}
                          onChange={(e) => setEditExtras(e.target.value)}
                          placeholder="Örn: Karamel sos, Fındık"
                        />
                      ) : it.extras?.length ? (
                        <div className="admin-extra-list">
                          {it.extras.map((extra) => (
                            <span key={extra} className="admin-extra-chip">
                              {extra}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* İşlem hücresi */}
                    <td>
                      {isEditing ? (
                        <div className="edit-actions">
                          <label className="file-picker compact">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setEditImage(e.target.files?.[0] ?? null)}
                            />
                            <span>Görsel Değiştir</span>
                          </label>
                          {editImagePreview && (
                            <div
                              className="image-crop-preview compact-preview"
                              onPointerDown={(e) => startCropDrag(e, "edit")}
                              onPointerMove={moveCropDrag}
                              onPointerUp={stopCropDrag}
                              onPointerCancel={stopCropDrag}
                              role="button"
                              tabIndex={0}
                              aria-label="Yeni görsel odak noktası seç"
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setEditImageFocus({ x: 0.5, y: 0.5 });
                                }
                              }}
                            >
                              <img
                                src={editImagePreview}
                                alt="Yeni ürün görseli"
                                style={{
                                  objectPosition: `${editImageFocus.x * 100}% ${editImageFocus.y * 100}%`,
                                }}
                              />
                              <span
                                className="image-crop-focus"
                                style={{
                                  left: `${editImageFocus.x * 100}%`,
                                  top: `${editImageFocus.y * 100}%`,
                                }}
                              />
                            </div>
                          )}
                          <div>
                            <button
                              className="btn-primary"
                              disabled={busy}
                              onClick={() => saveEdit(it.id)}
                              style={{ marginRight: 8 }}
                            >
                              Kaydet
                            </button>
                            <button className="btn" onClick={cancelEdit} disabled={busy}>
                              İptal
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            className="btn"
                            onClick={() => startEdit(it)}
                            disabled={busy}
                            style={{ marginRight: 8 }}
                          >
                            Düzenle
                          </button>
                          <button
                            className="btn-danger"
                            onClick={() => deleteItem(it.id)}
                            disabled={busy}
                          >
                            Sil
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
